import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

// Create Context
const SettingsContext = createContext();

// Custom hook to use the settings context
export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

// Constants for logo management
const getDefaultCompanyInfo = () => {
  return {
    name: '',
    address: '',
    city: 'Tanger',
    phone: '',
    email: '',
    website: '',
    rc: '',
    if: '',
    tp: '',
    ice: '',
    logo_path: '/logo.png' // Default logo path
  };
};

const mergeCompanyInfoWithDefaults = (info = {}) => {
  const defaults = getDefaultCompanyInfo();
  return {
    ...defaults,
    ...info,
    city: info?.city ?? defaults.city
  };
};

const DEFAULT_SETTINGS = {
  // General Settings
  language: 'fr',
  dateFormat: 'dd/MM/yyyy',
  timezone: 'Africa/Casablanca',
  autoSave: true,
  showTutorial: true,
  enableSounds: false,
  quickActions: true,

  // Notification Settings
  notifications: true,
  emailNotifications: true,
  smsNotifications: false,
  reminderTime: 30,
  rentalReminders: true,
  returnReminders: true,
  paymentReminders: true,

  // Appearance Settings
  sidebarColor: '#ECE7FB',
  pdfLogoOpacity: 0.1, // New setting for PDF background logo opacity
  availableSidebarColors: [
    '#115293', '#1b5e20', '#c21807', '#bf560c', '#7b1fa2',
    '#01579b', '#004d40', '#33691e', '#827717', '#e65100',
    '#0d47a1', '#1a237e', '#311b92', '#4a148c', '#880e4f',
    '#263238', '#37474f', '#455a64', '#546e7a', '#795548',
    '#00695c', '#2e7d32', '#558b2f', '#9e9d24', '#f57f17',
    '#d84315', '#6a1b9a', '#ad1457', '#283593'
  ],

  // Backup Settings
  autoBackup: false,
  backupFrequency: 'never',
  lastBackup: null,
  backupNotifications: true,
  autoBackupScope: 'account',
  autoBackupTime: '02:00',
  autoBackupDayOfWeek: 'monday',
  autoBackupDayOfMonth: 1,
  backupRetentionDays: 30,
  backupStorageTarget: 'local',
  backupCompression: true,
  backupNotifyFrom: '08:00',
  backupNotifyTo: '20:00',

  // Security Settings
  twoFactorAuth: false,
  autoLogout: true,
  sessionTimeout: 120,

  // Rental Settings
  autoCalculatePrices: true,
  taxInclusive: true,
  taxRate: 20,
  defaultCurrency: 'MAD',
  autoMaintenanceAlerts: true,
  fleetTracking: false,
  maintenanceThreshold: 10000
};

const PERSISTED_SETTING_KEYS = new Set([
  'language',
  'dateFormat',
  'timezone',
  'autoSave',
  'showTutorial',
  'enableSounds',
  'quickActions',
  'notifications',
  'emailNotifications',
  'smsNotifications',
  'reminderTime',
  'darkMode',
  'sidebarColor',
  'density',
  'collapsedSidebar',
  'showStatsDashboard',
  'quickAccessBar',
  'autoBackup',
  'backupFrequency',
  'lastBackup',
  'backupNotifications',
  'autoBackupScope',
  'autoBackupTime',
  'autoBackupDayOfWeek',
  'autoBackupDayOfMonth',
  'backupRetentionDays',
  'backupStorageTarget',
  'backupCompression',
  'backupNotifyFrom',
  'backupNotifyTo',
  'twoFactorAuth',
  'autoLogout',
  'sessionTimeout',
  'autoCalculatePrices',
  'taxInclusive',
  'taxRate',
  'defaultCurrency',
  'autoMaintenanceAlerts',
  'fleetTracking',
  'maintenanceThreshold',
  'highContrast',
  'largeText',
  'screenReader',
  'animationLevel',
  'dataCollection',
  'marketingEmails',
  'cookieConsent',
  'googleIntegration',
  'microsoftIntegration',
  'slackIntegration',
  'developerMode',
  'debugMode',
  'terms_conditions'
]);

// Settings Provider Component
export const SettingsProvider = ({ children }) => {
  const { currentUser, logout } = useAuth();
  const [settings, setSettings] = useState({});
  const [companyInfo, setCompanyInfo] = useState(mergeCompanyInfoWithDefaults()); // New state for company info
  const [isLoading, setIsLoading] = useState(true);
  const [uploadedLogos, setUploadedLogos] = useState([]); // New state for uploaded logos
  const logoutTimerRef = useRef(null);
  const settingsSaveTimeoutRef = useRef(null);
  const pendingSettingsRef = useRef({});

  const getDefaultSettings = useCallback(() => {
    return {
      ...DEFAULT_SETTINGS,
      availableSidebarColors: [...DEFAULT_SETTINGS.availableSidebarColors]
    };
  }, []);

  const normalizeSettings = useCallback((rawSettings) => {
    const normalized = { ...rawSettings };

    if ('autoBackupEnabled' in normalized && !('autoBackup' in normalized)) {
      normalized.autoBackup = normalized.autoBackupEnabled;
      delete normalized.autoBackupEnabled;
    }

    if ('autoBackupFrequency' in normalized && !('backupFrequency' in normalized)) {
      normalized.backupFrequency = normalized.autoBackupFrequency;
      delete normalized.autoBackupFrequency;
    }

    const defaults = DEFAULT_SETTINGS;
    Object.keys(defaults).forEach((key) => {
      if (!(key in normalized)) {
        return;
      }
      const defaultValue = defaults[key];
      const currentValue = normalized[key];
      if (typeof defaultValue === 'boolean') {
        if (currentValue === 0 || currentValue === 1 || currentValue === '0' || currentValue === '1') {
          normalized[key] = Boolean(Number(currentValue));
        }
      }
      if (typeof defaultValue === 'number' && typeof currentValue === 'string') {
        const parsed = Number(currentValue);
        if (Number.isFinite(parsed)) {
          normalized[key] = parsed;
        }
      }
    });

    if ('autoLogout' in normalized && typeof normalized.autoLogout !== 'boolean') {
      if (normalized.autoLogout === 'true') {
        normalized.autoLogout = true;
      } else if (normalized.autoLogout === 'false') {
        normalized.autoLogout = false;
      } else {
        normalized.autoLogout = Boolean(normalized.autoLogout);
      }
    }

    if ('sessionTimeout' in normalized) {
      const parsedTimeout = Number(normalized.sessionTimeout);
      if (Number.isFinite(parsedTimeout) && parsedTimeout > 0) {
        normalized.sessionTimeout = parsedTimeout;
      } else {
        delete normalized.sessionTimeout;
      }
    }

    return normalized;
  }, []);

  // Load settings from localStorage on initial render
  useEffect(() => {
    const loadSettings = () => {
      try {
        const savedSettings = localStorage.getItem('appSettings');
        const defaultSettings = getDefaultSettings();

        if (savedSettings) {
          const parsedSettings = JSON.parse(savedSettings);
          const mergedSettings = { ...defaultSettings, ...parsedSettings };
          setSettings(normalizeSettings(mergedSettings));
        } else {
          setSettings(defaultSettings);
          localStorage.setItem('appSettings', JSON.stringify(defaultSettings));
        }
      } catch (error) {

        setSettings(getDefaultSettings());
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [getDefaultSettings, normalizeSettings]);

  // Fetch settings from API once authenticated — merges global settings then user preferences on top
  useEffect(() => {
    const fetchServerSettings = async () => {
      if (!currentUser) return;
      try {
        const [globalResp, prefResp] = await Promise.allSettled([
          api.get('/settings'),
          api.get('/users/preferences')
        ]);
        const serverSettings = globalResp.status === 'fulfilled'
          ? (globalResp.value?.data?.data || globalResp.value?.data || {})
          : {};
        const userPrefs = prefResp.status === 'fulfilled'
          ? (prefResp.value?.data?.data || {})
          : (currentUser.preferences || {});

        setSettings((prev) => {
          // User preferences win over global settings win over defaults
          const merged = { ...getDefaultSettings(), ...prev, ...serverSettings, ...userPrefs };
          const normalized = normalizeSettings(merged);
          localStorage.setItem('appSettings', JSON.stringify(normalized));
          return normalized;
        });
      } catch (error) {
        // apply any preferences already on the currentUser object
        if (currentUser.preferences) {
          setSettings((prev) => ({ ...prev, ...currentUser.preferences }));
        }
      }
    };

    fetchServerSettings();
    // Re-run only when the logged-in user actually changes (login/logout),
    // not on every merged-object update from e.g. a profile save — those
    // create a new currentUser reference without changing who's logged in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, getDefaultSettings, normalizeSettings]);

  // Get company info from API
  const getCompanyInfo = useCallback(async () => {
    try {
      const response = await api.get('/settings/company-info');
      const fetchedCompanyInfo = response.data.data || {};
      const normalizedCompanyInfo = mergeCompanyInfoWithDefaults(fetchedCompanyInfo);
      setCompanyInfo(normalizedCompanyInfo);
      return { success: true, data: normalizedCompanyInfo };
    } catch (error) {

      const defaultCompany = mergeCompanyInfoWithDefaults();
      setCompanyInfo(defaultCompany);
      return { success: false, data: defaultCompany };
    }
  }, []);

  // Fetch company info and uploaded logos when the logged-in user changes
  // (login/logout) — keyed on the user's id, not the whole currentUser
  // object, so an unrelated profile-save update (new object, same id)
  // doesn't re-trigger these fetches.
  useEffect(() => {
    if (currentUser) {
      getCompanyInfo();
      getUploadedLogos(); // Fetch uploaded logos when user logs in
    } else {
      setCompanyInfo(getDefaultCompanyInfo());
      setUploadedLogos([]); // Clear logos if no user
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, getCompanyInfo]);

  // Keys that are personal to each user (saved to users.preferences, not global app_settings)
  const USER_PREFERENCE_KEYS = new Set(['sidebarColor', 'darkMode', 'density', 'language', 'timezone']);

  // Update settings
  const updateSettings = useCallback((newSettings) => {
    setSettings((prev) => {
      const updated = { ...prev, ...newSettings };
      const normalized = normalizeSettings(updated);
      localStorage.setItem('appSettings', JSON.stringify(normalized));
      return normalized;
    });
    if (currentUser) {
      pendingSettingsRef.current = {
        ...pendingSettingsRef.current,
        ...newSettings
      };
      if (settingsSaveTimeoutRef.current) {
        clearTimeout(settingsSaveTimeoutRef.current);
      }
      settingsSaveTimeoutRef.current = setTimeout(async () => {
        const pending = pendingSettingsRef.current;
        pendingSettingsRef.current = {};

        // Split into per-user preferences and global settings
        const userPrefsPayload = Object.fromEntries(
          Object.entries(pending).filter(([key]) => USER_PREFERENCE_KEYS.has(key))
        );
        const globalPayload = Object.fromEntries(
          Object.entries(pending).filter(([key]) => PERSISTED_SETTING_KEYS.has(key) && !USER_PREFERENCE_KEYS.has(key))
        );

        const saves = [];
        if (Object.keys(userPrefsPayload).length > 0) {
          saves.push(api.put('/users/preferences', userPrefsPayload).catch(() => {}));
        }
        if (Object.keys(globalPayload).length > 0) {
          saves.push(api.put('/settings', globalPayload).catch(() => {}));
        }
        await Promise.all(saves);
      }, 600);
    }
  }, [normalizeSettings, currentUser]);

  // Update settings without persisting to localStorage
  const updateSettingsInMemory = useCallback((newSettings) => {
    setSettings((prev) => ({ ...prev, ...newSettings }));
  }, []);

  useEffect(() => {
    return () => {
      if (settingsSaveTimeoutRef.current) {
        clearTimeout(settingsSaveTimeoutRef.current);
        settingsSaveTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!currentUser) {
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
      return;
    }

    const autoLogoutEnabled = settings.autoLogout !== false;
    const sessionTimeoutMinutes = Math.max(10, settings.sessionTimeout || 120);
    const sessionTimeoutMs = sessionTimeoutMinutes * 60 * 1000;

    const clearLogoutTimer = () => {
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
    };

    const scheduleLogout = () => {
      clearLogoutTimer();
      if (!autoLogoutEnabled) {
        return;
      }
      logoutTimerRef.current = setTimeout(() => {
        logout();
      }, sessionTimeoutMs);
    };

    const handleActivity = () => {
      if (!autoLogoutEnabled) {
        return;
      }
      scheduleLogout();
    };

    scheduleLogout();

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });

    return () => {
      clearLogoutTimer();
      events.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
    };
  }, [currentUser, logout, settings.autoLogout, settings.sessionTimeout]);

  // Upload logo with smart optimization
  const uploadLogo = useCallback(async (file) => {
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await api.post('/settings/upload-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setCompanyInfo((prev) => ({ ...prev, logo_path: response.data.logo.file_path }));
      setUploadedLogos((prev) => [...prev, response.data.logo]);

      return response.data.logo.file_path;
    } catch (error) {

      throw error;
    }
  }, []);

  // Get uploaded logos
  const getUploadedLogos = useCallback(async () => {
    try {
      const response = await api.get('/settings/uploaded-logos');
      setUploadedLogos(response.data.data);
      return response.data.data;
    } catch (error) {

      return [];
    }
  }, []);

  // Select logo
  const selectLogo = useCallback(async (logoPath) => {
    try {
      const response = await api.post('/settings/select-logo', { logoPath });
      setCompanyInfo((prev) => ({ ...prev, logo_path: response.data.logoPath }));
      return response.data.logoPath;
    } catch (error) {

      throw new Error('Failed to select logo');
    }
  }, []);

  // Update company info
  const updateCompanyInfo = useCallback(async (newCompanyInfo) => {
    try {
      const response = await api.put('settings/company-info', newCompanyInfo);
      const defaultCity = getDefaultCompanyInfo().city;
      setCompanyInfo((prev) => ({
        ...prev,
        ...newCompanyInfo,
        city: newCompanyInfo.city ?? prev.city ?? defaultCity
      }));
      return response.data;
    } catch (error) {

      throw new Error('Failed to save company information');
    }
  }, []);

  // Delete specific logo
  const deleteLogo = useCallback(async (logoId, logoPath = null) => {
    try {
      await api.delete(`/settings/delete-logo/${logoId}`);
      setUploadedLogos((prev) => prev.filter((logo) => logo.id !== logoId));
      // If the deleted logo was the currently active one, clear it from companyInfo
      if (companyInfo.logo_path && logoPath && companyInfo.logo_path === logoPath) {
        setCompanyInfo((prev) => ({ ...prev, logo_path: '/logo.png' }));
      }
      return true;
    } catch (error) {

      throw new Error('Failed to delete logo');
    }
  }, [companyInfo]);

  // Export data
  const exportData = useCallback(async (format = 'json') => {
    try {
      const data = {
        settings,
        companyInfo,
        exportDate: new Date().toISOString()
      };

      let blob;
      let filename;

      if (format === 'json') {
        blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        filename = `car-rental-backup-${new Date().toISOString().split('T')[0]}.json`;
      } else if (format === 'csv') {
        const csvData = Object.entries(data.companyInfo).
        map(([key, value]) => `${key},${value}`).
        join('\n');
        blob = new Blob([csvData], { type: 'text/csv' });
        filename = `company-info-${new Date().toISOString().split('T')[0]}.csv`;
      } else {
        throw new Error('Unsupported export format');
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);

      updateSettings({ lastBackup: new Date().toLocaleString() });

      return true;
    } catch (error) {

      throw new Error(`Export failed: ${error.message}`);
    }
    // companyInfo is read above (data.companyInfo) but was missing from
    // deps — a real staleness bug: without it, an export triggered right
    // after a company-info update could still bundle the old value.
  }, [settings, companyInfo, updateSettings]);

  // Import data
  const importData = useCallback(async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = JSON.parse(e.target.result);

          if (!data.settings || !data.companyInfo) {
            throw new Error('Invalid backup file format');
          }

          if (data.settings) {
            setSettings((prev) => {
              const updated = { ...prev, ...data.settings };
              const normalized = normalizeSettings(updated);
              localStorage.setItem('appSettings', JSON.stringify(normalized));
              return normalized;
            });
          }

          if (data.companyInfo) {
            await updateCompanyInfo(data.companyInfo); // Use API to update company info
          }

          resolve();
        } catch (error) {
          reject(new Error(`Failed to import data: ${error.message}`));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
    // normalizeSettings and updateCompanyInfo are called above but were
    // missing from deps — normalizeSettings is stable so this was latent,
    // but a real bug in principle (a stale updateCompanyInfo would import
    // into the wrong place if that function's identity ever changed).
  }, [normalizeSettings, updateCompanyInfo]);

  // Context value
  const value = useMemo(() => ({
    settings,
    isLoading,
    updateSettings,
    updateSettingsInMemory,
    companyInfo, // Expose companyInfo from state
    getCompanyInfo,
    updateCompanyInfo,
    uploadLogo,
    selectLogo,
    getUploadedLogos,
    deleteLogo,
    uploadedLogos, // Expose uploadedLogos from state
    exportData,
    importData
  }), [
    settings,
    isLoading,
    updateSettings,
    updateSettingsInMemory,
    companyInfo,
    getCompanyInfo,
    updateCompanyInfo,
    uploadLogo,
    selectLogo,
    getUploadedLogos,
    deleteLogo,
    uploadedLogos,
    exportData,
    importData
  ]);
  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>);

};

export default SettingsContext;
